import React, { useCallback, useState, useEffect } from "react";
import { Form, Modal } from "@components";
import { Box, Button } from "@material-ui/core";
import { Manager } from "@components/form/state/types";
import { InfraAppModel } from "@api/types/infraapp";
import api from "@api";
import { useApproverDistroSelect, useInfrastructureSelect } from "@hooks";
import { Field } from "../../../../../../components/form/fields/field";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import { useInfraAppStore } from "../../../../../../store/hooksy";
import { alertService } from "@services";

interface Props {
  open: boolean;
  onCancel: () => void;
  form: Manager<InfraAppModel>;
}

type Key = keyof InfraAppModel;

export const InfraAppDetail: React.FC<Props> = ({ open, onCancel, form }) => {
  const [, setInfraAppStore] = useInfraAppStore();

  let infraApp: InfraAppModel = {
    id: 0,
    name: "",
    approver: "",
    isActive: false,
  };

  // custom hooks
  const {
    approverDistros,
    isFetchingApproverDistros,
    selectedApproverDistro,
    setSelectedApproverDistro,
    handleApproverDistroTypeChange,
  } = useApproverDistroSelect(form.values.approverDistro, null, null, null);

  const {
    infrastructures,
    isFetchinginfrastructures,
    selectedInfrastructure,
    setSelectedInfrastructure,
    handleInfrastructureTypeChange,
  } = useInfrastructureSelect(form.values.infrastructure);

  function clearAndCloseModelData() {
    infraApp.id = 0;
    infraApp.name = "";
    infraApp.approver = "";
    infraApp.approverDistro = null;
    infraApp.isActive = false;
    form.reset();
    onCancel();
  }

  const [isSubmitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (selectedApproverDistro) {
      form.set(Form.target.VALUE, ["approver", ""]);
    }
  }, [selectedApproverDistro]);

  const handleSubmit = useCallback(() => {
    Promise.all(Object.entries(form.values).map(validate)).then(
      (validations) => {
        const valid = validations.reduce((valid, [name, error]) => {
          return valid && !error;
        }, true);
        if (valid) {
          submitData();
        } else {
          validations.forEach(([name, error]) => {
            form.set(Form.target.ERROR, [name, error]);
          });
        }
      }
    );
  }, [form, selectedApproverDistro, selectedInfrastructure]);

  function submitData() {
    infraApp.id = form.values.id;
    infraApp.name = form.values.name;
    infraApp.approver = form.values.approver;
    infraApp.approverDistro = selectedApproverDistro
      ? selectedApproverDistro
      : null;
    infraApp.infrastructure = selectedInfrastructure
      ? selectedInfrastructure
      : null;
    infraApp.isActive = form.values.isActive;
    setSubmitting(true);
    if (infraApp.id !== 0) {
      // EDIT
      api.infraapp
        .put(infraApp.id, infraApp)
        .then((response) => {
          alertService.success({
            msg: "Infra app has been successfully updated.",
          });
          clearAndCloseModelData();
          setInfraAppStore({ fetchList: true });
        })
        .catch((error) => {
          if (error.response) {
            alertService.danger({ statusCode: error.response.status });
          }
        })
        .then(() => {
          setSubmitting(false);
        });
    } else {
      // CREATE
      api.infraapp
        .post(infraApp)
        .then((response) => {
          alertService.success({
            msg: "Infra app has been successfully saved.",
          });
          clearAndCloseModelData();
          setInfraAppStore({ fetchList: true });
        })
        .catch((error) => {
          if (error.response) {
            alertService.danger({ statusCode: error.response.status });
          }
        })
        .then(() => {
          setSubmitting(false);
        });
    }
  }

  function validate([name, value]: [string, any]): Promise<[Key, Error?]> {
    switch (name) {
      case "name":
        return Promise.resolve([name as Key, Form.Validators.hasValue(value)]);
      case "approver":
        if (value) {
          return Promise.resolve([
            name as Key,
            Form.Validators.isEmail(value as string),
          ]);
        }
      default:
        return Promise.resolve([name as Key]);
    }
  }

  function onChange(name: Key, value: any): void {
    form.set(Form.target.VALUE, [name, value]);
    validate([name, value]).then(([name, error]) => {
      form.set(Form.target.ERROR, [name, error]);
    });
    if (name === "approver") setSelectedApproverDistro(0);
  }

  const modalTitle = form.values.id !== 0 ? "Update" : "Add New";
  const submitText = isSubmitting ? "Submitting..." : "Submit";

  let approverDistroMenu = null;
  if (isFetchingApproverDistros) {
    approverDistroMenu = <MenuItem value={0}>Please wait...</MenuItem>;
  } else {
    approverDistroMenu = approverDistros.map((item: any, key) => (
      <MenuItem key={key} value={item.value}>
        {item.label}
      </MenuItem>
    ));
  }

  let infrastructureMenu = null;
  if (isFetchinginfrastructures) {
    infrastructureMenu = <MenuItem value={0}>Please wait...</MenuItem>;
  } else {
    infrastructureMenu = infrastructures.map((item: any, key) => (
      <MenuItem key={key} value={item.value}>
        {item.label}
      </MenuItem>
    ));
  }

  return (
    <Modal
      isOpen={open}
      title={`${modalTitle} Infrastructure Application`}
      closeModal={() => clearAndCloseModelData()}
      size="xs"
    >
      <Form>
        <Form.Textbox
          label="App Name"
          name="name"
          onChange={onChange}
          size={12}
          error={form.errors.name}
          value={form.values.name}
          placeholder="Enter app name"
          required
        />
        <Form.Textbox
          label="Approver"
          name="approver"
          onChange={onChange}
          size={12}
          error={form.errors.approver}
          value={form.values.approver}
          placeholder="Enter approver"
        />
        <Field label="Approver Distro" size={12}>
          <Select
            name="approverDistro"
            value={selectedApproverDistro}
            onChange={handleApproverDistroTypeChange}
            disabled={isFetchingApproverDistros}
          >
            {approverDistroMenu}
          </Select>
        </Field>
        <Field label="Infrastructure" size={12}>
          <Select
            name="infrastructure"
            value={selectedInfrastructure}
            onChange={handleInfrastructureTypeChange}
            disabled={isFetchinginfrastructures}
          >
            {infrastructureMenu}
          </Select>
        </Field>
        <Form.Checkbox
          label="Is Active"
          name="isActive"
          onChange={onChange}
          size={6}
          value={form.values.isActive}
        />
      </Form>
      <Box textAlign="right" mt={4}>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => clearAndCloseModelData()}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          type="button"
          onClick={handleSubmit}
          style={{ marginLeft: "40px" }}
          disabled={isSubmitting}
        >
          {submitText}
        </Button>
      </Box>
    </Modal>
  );
};
